import { Link, useLocation } from "@tanstack/react-router";
import {
  Book,
  History,
  LayoutDashboard,
  Link2,
  RefreshCw,
  ShieldUser,
  Upload,
  Wallet,
  Wrench,
} from "lucide-react";
import type { PropsWithChildren } from "react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { SuperAdminOnly } from "@/components/shared/miscellaneous/superadmin-only";
import { ManuallyUploadTransactions } from "./manually-upload-transactions";
import { TriggerReconciledTransactions } from "./trigger-reconciled-transactions";
import { TriggerTransactionSuggestions } from "./trigger-transaction-suggestions";

type Props = PropsWithChildren;

export const SuperadminTools = ({ children }: Props) => {
  const location = useLocation();

  const pathParts = location.pathname.split("/").filter(Boolean);
  const companySlug = pathParts.length > 0 ? pathParts[0] : undefined;

  const [
    isReconciledTransactionsDialogOpen,
    setIsReconciledTransactionsDialogOpen,
  ] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [
    isManuallyUploadTransactionsDialogOpen,
    setIsManuallyUploadTransactionsDialogOpen,
  ] = useState(false);
  const [
    isTransactionSuggestionsDialogOpen,
    setIsTransactionSuggestionsDialogOpen,
  ] = useState(false);

  const handleRunReconciledTransactions = () => {
    setIsDropdownOpen(false);
    setTimeout(() => {
      setIsReconciledTransactionsDialogOpen(true);
    }, 100);
  };

  const handleManuallyUploadTransactions = () => {
    setIsDropdownOpen(false);
    setTimeout(() => {
      setIsManuallyUploadTransactionsDialogOpen(true);
    }, 100);
  };

  const handleCreateTransactionSuggestion = () => {
    setIsDropdownOpen(false);
    setTimeout(() => {
      setIsTransactionSuggestionsDialogOpen(true);
    }, 100);
  };

  return (
    <SuperAdminOnly>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          {children ? (
            children
          ) : (
            <Button variant="outline" size="sm">
              <Wrench className="h-4 w-4 mr-2" />
              Superadmin Tools
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          align="start"
          className="border-l-2 border-l-pink-400"
        >
          <DropdownMenuLabel className="text-pink-600 text-xs font-medium">
            Superadmin Tools
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {companySlug && (
            <>
              <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                Pages
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link
                  to="/$company/buchhaltung/mandant-bearbeiten"
                  params={{
                    company: companySlug,
                  }}
                >
                  <ShieldUser className="h-4 w-4 mr-2" />
                  Manage Company
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/$company/buchhaltung/einstellungen/exporte"
                  params={{
                    company: companySlug,
                  }}
                >
                  <History className="h-4 w-4 mr-2" />
                  Export History
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/$company/buchhaltung/ledger"
                  params={{
                    company: companySlug,
                  }}
                >
                  <Book className="h-4 w-4 mr-2" />
                  Ledger
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/$company/buchhaltung/einstellungen/kontenplan"
                  params={{
                    company: companySlug,
                  }}
                >
                  <Book className="h-4 w-4 mr-2" />
                  Chart of Accounts
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                Reporting (Preview)
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link
                  to="/$company/unternehmen/reporting/dashboard"
                  params={{
                    company: companySlug,
                  }}
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/$company/unternehmen/reporting/accounts"
                  params={{
                    company: companySlug,
                  }}
                >
                  <Wallet className="h-4 w-4 mr-2" />
                  Accounts
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
            Actions
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={handleRunReconciledTransactions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Trigger Reconciled Transactions
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleManuallyUploadTransactions}>
            <Upload className="h-4 w-4 mr-2" />
            Manually Upload Transactions
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCreateTransactionSuggestion}>
            <Link2 className="h-4 w-4 mr-2" />
            Create Suggested Attachment
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <TriggerReconciledTransactions
        isDialogOpen={isReconciledTransactionsDialogOpen}
        setIsDialogOpen={setIsReconciledTransactionsDialogOpen}
      />
      <ManuallyUploadTransactions
        isDialogOpen={isManuallyUploadTransactionsDialogOpen}
        setIsDialogOpen={setIsManuallyUploadTransactionsDialogOpen}
      />
      <TriggerTransactionSuggestions
        isDialogOpen={isTransactionSuggestionsDialogOpen}
        setIsDialogOpen={setIsTransactionSuggestionsDialogOpen}
      />
    </SuperAdminOnly>
  );
};
